import { useState, useCallback, useRef } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import { processTopicImport } from '../../utils/trainingUtils';
import { TopicStyle, TrainingTopicInput } from '../../types/trainingTypes';
import '../../css/ImportTopicsModal.css';

interface ImportTopicsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const VALID_STYLES: TopicStyle[] = ['Video', 'PDF', 'Poster', 'FatalGram', 'Other', 'Uncategorized'];

const TEMPLATE_CSV = `ID,Style,Description,Time Value,Categories
VID-001,Video,Electrical Safety Overview,15,"Electrical Safety,General Safety"
PDF-002,PDF,Lockout Tagout Procedures,10,Electrical Safety
POST-003,Poster,PPE Requirements,5,General Safety`;

/**
 * ImportTopicsModal — CSV file upload for bulk topic import.
 * Parses CSV, detects duplicate IDs, and imports new topics.
 *
 * Validates: Requirements 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7
 */
export const ImportTopicsModal = ({ isOpen, onClose }: ImportTopicsModalProps) => {
    const { topics, createTopic } = useSafetyTrainingContext();
    const { companyUser, companyOwner, businessAdmin } = useUserContext();
    const userCompanyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';
    const companyId = (!userCompanyId && businessAdmin) ? 'library-template' : userCompanyId;

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [parseError, setParseError] = useState<string | null>(null);
    const [importResult, setImportResult] = useState<{
        importedCount: number;
        skippedCount: number;
        skippedIds: string[];
    } | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const resetState = useCallback(() => {
        setSelectedFile(null);
        setParseError(null);
        setImportResult(null);
        setIsImporting(false);
        setIsDragOver(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const handleClose = useCallback(() => {
        resetState();
        onClose();
    }, [resetState, onClose]);

    const handleFileSelect = useCallback((file: File) => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (ext !== 'csv' && ext !== 'xlsx' && ext !== 'xls') {
            setParseError('Please upload a CSV or Excel file (.csv, .xlsx, .xls).');
            return;
        }
        setSelectedFile(file);
        setParseError(null);
        setImportResult(null);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileSelect(file);
    }, [handleFileSelect]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFileSelect(file);
    }, [handleFileSelect]);

    const handleRemoveFile = useCallback(() => {
        setSelectedFile(null);
        setParseError(null);
        setImportResult(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const parseCSV = (text: string): TrainingTopicInput[] => {
        const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        if (lines.length < 2) {
            throw new Error('CSV must have a header row and at least one data row.');
        }

        // Parse header
        const header = lines[0].split(',').map(h => h.trim().toLowerCase());
        const idIdx = header.indexOf('id');
        const styleIdx = header.indexOf('style');
        const descIdx = header.indexOf('description');
        const timeIdx = header.indexOf('time value');
        const catIdx = header.indexOf('categories');

        if (idIdx === -1 || styleIdx === -1 || descIdx === -1 || timeIdx === -1) {
            throw new Error('Missing required columns. Required: ID, Style, Description, Time Value.');
        }

        const rows: TrainingTopicInput[] = [];
        for (let i = 1; i < lines.length; i++) {
            const fields = parseCSVLine(lines[i]);
            const topicId = fields[idIdx]?.trim();
            const styleRaw = fields[styleIdx]?.trim();
            const description = fields[descIdx]?.trim();
            const timeValueStr = fields[timeIdx]?.trim();

            if (!topicId || !styleRaw || !description) continue;

            // Validate style
            const style = VALID_STYLES.find(s => s.toLowerCase() === styleRaw.toLowerCase()) || 'Other';
            const timeValue = parseInt(timeValueStr, 10) || 0;

            // Parse optional categories
            let categoryIds: string[] | undefined;
            if (catIdx !== -1 && fields[catIdx]) {
                const catStr = fields[catIdx].trim();
                if (catStr) {
                    categoryIds = catStr.split(',').map(c => c.trim()).filter(c => c.length > 0);
                }
            }

            rows.push({
                topicId,
                style: style as TopicStyle,
                description,
                timeValue,
                categoryIds,
            });
        }

        return rows;
    };

    /** Simple CSV line parser that handles quoted fields with commas */
    const parseCSVLine = (line: string): string[] => {
        const fields: string[] = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
                inQuotes = !inQuotes;
            } else if (ch === ',' && !inQuotes) {
                fields.push(current);
                current = '';
            } else {
                current += ch;
            }
        }
        fields.push(current);
        return fields;
    };

    const handleImport = useCallback(async () => {
        if (!selectedFile) return;
        setIsImporting(true);
        setParseError(null);

        try {
            const text = await selectedFile.text();
            const parsedRows = parseCSV(text);

            if (parsedRows.length === 0) {
                setParseError('No valid rows found in the file.');
                setIsImporting(false);
                return;
            }

            // Detect duplicates using processTopicImport
            const existingTopicIds = topics.map(t => t.topicId);
            const result = processTopicImport(parsedRows, existingTopicIds);

            // Import non-duplicate topics
            for (const row of result.imported) {
                await createTopic({
                    companyId,
                    topicId: row.topicId,
                    style: row.style,
                    description: row.description,
                    timeValue: row.timeValue,
                    categoryIds: row.categoryIds || null,
                });
            }

            setImportResult({
                importedCount: result.imported.length,
                skippedCount: result.skipped.length,
                skippedIds: result.skipped.map(s => s.topicId),
            });
        } catch (err: any) {
            setParseError(err?.message || 'Failed to parse or import the file.');
        } finally {
            setIsImporting(false);
        }
    }, [selectedFile, topics, createTopic, companyId]);

    const handleDownloadTemplate = useCallback(() => {
        const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'training_topics_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, []);

    if (!isOpen) return null;

    return (
        <div className="itm-overlay" onClick={handleClose}>
            <div className="itm-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="itm-header">
                    <h3>Import Topics</h3>
                    <button className="itm-close-btn" onClick={handleClose} aria-label="Close">
                        ×
                    </button>
                </div>

                {/* Body */}
                <div className="itm-body">
                    {/* Import result display */}
                    {importResult && (
                        <div className="itm-result">
                            <div className="itm-result-summary">
                                <span className="itm-result-imported">
                                    ✓ {importResult.importedCount} topic{importResult.importedCount !== 1 ? 's' : ''} imported
                                </span>
                                {importResult.skippedCount > 0 && (
                                    <span className="itm-result-skipped">
                                        ⚠ {importResult.skippedCount} skipped (duplicate IDs)
                                    </span>
                                )}
                            </div>
                            {importResult.skippedIds.length > 0 && (
                                <div className="itm-skipped-list">
                                    <p className="itm-skipped-label">Skipped IDs:</p>
                                    <div className="itm-skipped-ids">
                                        {importResult.skippedIds.map(id => (
                                            <span key={id} className="itm-skipped-id-pill">{id}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* File upload area (hidden after successful import) */}
                    {!importResult && (
                        <>
                            {parseError && (
                                <div className="itm-error-banner">{parseError}</div>
                            )}

                            <div className="itm-columns-info">
                                <p className="itm-columns-label">Required columns:</p>
                                <div className="itm-columns-list">
                                    <span className="itm-column-pill required">ID</span>
                                    <span className="itm-column-pill required">Style</span>
                                    <span className="itm-column-pill required">Description</span>
                                    <span className="itm-column-pill required">Time Value</span>
                                    <span className="itm-column-pill optional">Categories (optional, comma-separated)</span>
                                </div>
                            </div>

                            <div
                                className={`itm-dropzone ${isDragOver ? 'drag-over' : ''}`}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onClick={() => fileInputRef.current?.click()}
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        fileInputRef.current?.click();
                                    }
                                }}
                            >
                                <div className="itm-dropzone-icon">📄</div>
                                <p className="itm-dropzone-text">
                                    Drag &amp; drop a CSV file here, or click to browse
                                </p>
                                <p className="itm-dropzone-hint">
                                    CSV or Excel files accepted
                                </p>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                style={{ display: 'none' }}
                                onChange={handleFileInputChange}
                            />

                            {selectedFile && (
                                <div className="itm-file-preview">
                                    <span className="itm-file-preview-name">{selectedFile.name}</span>
                                    <span className="itm-file-preview-size">
                                        {(selectedFile.size / 1024).toFixed(1)} KB
                                    </span>
                                    <button
                                        className="itm-file-remove-btn"
                                        onClick={handleRemoveFile}
                                        title="Remove file"
                                    >
                                        ×
                                    </button>
                                </div>
                            )}

                            <button className="itm-template-link" onClick={handleDownloadTemplate}>
                                ↓ Download template CSV
                            </button>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="itm-footer">
                    <button className="itm-btn-cancel" onClick={handleClose} disabled={isImporting}>
                        {importResult ? 'Close' : 'Cancel'}
                    </button>
                    {!importResult && (
                        <button
                            className="itm-btn-import"
                            onClick={handleImport}
                            disabled={!selectedFile || isImporting}
                        >
                            {isImporting ? 'Importing...' : 'Import Topics'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
